import React from "react";
import { Card, Empty, Typography } from "antd";
import type { GlobalToken } from "antd/es/theme/interface";
import dayjs, { Dayjs } from "dayjs";
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { PromillePoint, SoberInfo } from "./types";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

type PromilleTimelineCardProps = {
    timeline: PromillePoint[];
    selectedDay: Dayjs;
    hasSelectedDayData: boolean;
    soberInfo: SoberInfo;
    token: GlobalToken;
};

const PromilleTimelineCard: React.FC<PromilleTimelineCardProps> = ({
    timeline,
    selectedDay,
    hasSelectedDayData,
    soberInfo,
    token,
}) => {
    const { t } = useTranslation();

    return (
        <Card title={t("drinkStats.timeline.title")}>
            {timeline.length > 1 && hasSelectedDayData ? (
                <>
                    <div className="analytics-chart">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={timeline} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                            <XAxis
                                dataKey="timestamp"
                                type="number"
                                domain={["auto", "auto"]}
                                tickFormatter={(value) => dayjs(value).format("HH:mm")}
                                tick={{ fill: token.colorTextSecondary, fontSize: 12 }}
                                tickMargin={8}
                            />
                            <YAxis
                                width={48}
                                tick={{ fill: token.colorTextSecondary, fontSize: 12 }}
                                tickMargin={8}
                                allowDecimals
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: token.colorBgElevated,
                                    color: token.colorText,
                                }}
                                labelStyle={{
                                    color: token.colorTextSecondary,
                                }}
                                wrapperClassName="dark-tooltip"
                                labelFormatter={(value) => dayjs(value as number).format("DD MMM YYYY HH:mm")}
                                formatter={(value: number) => [`${value.toFixed(3)} ‰`, t("drinkStats.timeline.tooltipLabel")]}
                            />
                            <Legend />
                            <Line type="monotone" dataKey="promille" name={t("drinkStats.timeline.axisLabel")} stroke={token.colorPrimary} dot />
                        </LineChart>
                    </ResponsiveContainer>
                    </div>
                    {soberInfo.actual && (
                        <Text type="secondary" style={{ display: "block", marginTop: 12 }}>
                            {t("drinkStats.timeline.soberAt", {
                                time: dayjs(soberInfo.actual).format("DD MMM YYYY HH:mm"),
                            })}
                        </Text>
                    )}
                    {!soberInfo.actual && soberInfo.projected && (
                        <Text type="secondary" style={{ display: "block", marginTop: 12 }}>
                            {t("drinkStats.timeline.soberEta", {
                                time: dayjs(soberInfo.projected).format("DD MMM YYYY HH:mm"),
                            })}
                        </Text>
                    )}
                </>
            ) : (
                <Empty
                    description={t("drinkStats.timeline.empty", {
                        date: selectedDay.format("DD MMM YYYY"),
                    })}
                />
            )}
        </Card>
    );
};

export default PromilleTimelineCard;
